<!--
  PurchaseRequestModal — 바우처 구매(충전) 요청 (POST /voucher/org/{org}/purchases)
  · 통화 선택지는 GET /common/currencies 의 code 목록, 기본값은 기관의 voucher_currency. rate_of_receive 는 화면에 쓰지 않는다
  · 201 응답의 reference(VP-{idx}) 를 입금 참조번호로 안내. fx_notice=true 면 환산 안내
-->
<template>
  <a-modal :open="true" title="바우처 구매 요청" sub="요청 뒤 안내되는 참조번호로 입금하면 관리자가 확인해 적립합니다" size="md" :persistent="submitting" @close="$emit('close')">
    <template v-if="!result">
      <div class="p-form">
        <div class="p-form__row">
          <div class="p-field">
            <label class="p-field__label">금액 <span class="p-field__req">*</span></label>
            <input v-model.trim="form.amount" type="number" min="0" step="0.01" class="p-input p-input--mono" placeholder="예: 5000" />
          </div>
          <div class="p-field">
            <label class="p-field__label">통화 <span class="p-field__req">*</span></label>
            <select v-model="form.currency_code" class="p-input p-select" :disabled="currenciesLoading">
              <option v-if="currenciesLoading" value="">불러오는 중...</option>
              <option v-for="c in currencies" :key="c.code" :value="c.code">{{ c.code }}</option>
            </select>
          </div>
        </div>
        <div class="p-field">
          <label class="p-field__label">메모</label>
          <input v-model.trim="form.memo" type="text" class="p-input" maxlength="200" placeholder="입금 예정일 등 (선택)" />
        </div>

        <div class="p-alert p-alert--info">
          <div class="p-alert__title">입금 안내</div>
          <div class="p-alert__body">
            바우처 통화는 <strong>{{ orgCurrency || '-' }}</strong> 입니다. 환율 변동 없이 그대로 적립되려면 {{ orgCurrency || 'USD' }} 로 입금하세요.
            요청만으로 잔액이 오르지 않고, 관리자가 입금을 확인해 확정한 실입금액이 적립됩니다.
            <template v-if="discountRate > 0"><br />학생 등록 시 정가의 <strong>{{ chargePercent }}%</strong> 로 차감됩니다(기관 할인 {{ discountRate }}%).</template>
          </div>
        </div>
        <div v-if="isForeign" class="p-alert p-alert--warn">
          <div class="p-alert__body">선택한 통화({{ form.currency_code }})는 바우처 통화와 다릅니다. 바우처 통화로 환산해 적립되며, 적립액은 입금 확정 시점의 환율에 따라 달라집니다.</div>
        </div>
        <div v-if="error" class="p-alert p-alert--err"><div class="p-alert__body">{{ error }}</div></div>
      </div>
    </template>

    <template v-else>
      <div class="p-done">
        <div class="p-done__ref-label">입금 참조번호</div>
        <div class="p-done__ref">{{ result.reference }}</div>
        <dl class="p-kv p-mt-16">
          <dt>요청 금액</dt><dd class="p-mono">{{ fmtAmount(result.requested_amount, result.requested_currency) }}</dd>
          <dt>바우처 통화</dt><dd class="p-mono">{{ result.voucher_currency || orgCurrency }}</dd>
          <dt>상태</dt><dd>요청됨 — 관리자 입금 확인 대기</dd>
          <dt>요청 일시</dt><dd class="p-mono">{{ fmtDateTime(result.created_at) }}</dd>
        </dl>
        <div v-if="result.deposit_guide" class="p-alert p-alert--info p-mt-16">
          <div class="p-alert__title">입금 방법</div>
          <div class="p-alert__body p-pre">{{ result.deposit_guide }}</div>
        </div>
        <div v-if="result.fx_notice" class="p-alert p-alert--warn p-mt-16">
          <div class="p-alert__body">바우처 통화로 환산해 적립되며 환율에 따라 달라집니다. USD 입금을 권장합니다.</div>
        </div>
        <div class="p-note">입금할 때 위 참조번호를 적어 주세요. 입금 전에는 목록에서 요청을 철회할 수 있습니다.</div>
      </div>
    </template>

    <template #foot>
      <template v-if="!result">
        <a-btn kind="ghost" :disabled="submitting" @click="$emit('close')">취소</a-btn>
        <a-btn kind="primary" :disabled="!canSubmit || submitting" @click="submit">{{ submitting ? '요청 중...' : '구매 요청' }}</a-btn>
      </template>
      <a-btn v-else kind="primary" @click="$emit('close')">닫기</a-btn>
    </template>
  </a-modal>
</template>

<script>
import AModal from '@/components/Pineapple/AModal.vue';
import ABtn from '@/components/Pineapple/ABtn.vue';
import { fmtAmount, fmtDateTime, voucherErrorText } from '../orgUtils';

export default {
  name: 'PurchaseRequestModal',
  components: { AModal, ABtn },
  props: {
    orgIdx: { type: [Number, String], required: true },
    organization: { type: Object, default: null },
  },
  emits: ['close', 'requested'],
  data() {
    return {
      currencies: [],
      currenciesLoading: false,
      form: { amount: '', currency_code: '', memo: '' },
      submitting: false,
      error: '',
      result: null,
    };
  },
  computed: {
    orgCurrency() { return this.organization ? this.organization.voucher_currency || '' : ''; },
    discountRate() { return this.organization ? Number(this.organization.voucher_discount_rate) || 0 : 0; },
    chargePercent() { return Math.round((100 - this.discountRate) * 100) / 100; },
    isForeign() { return !!(this.form.currency_code && this.orgCurrency && this.form.currency_code !== this.orgCurrency); },
    canSubmit() { return Number(this.form.amount) > 0 && !!this.form.currency_code; },
  },
  mounted() {
    this.loadCurrencies();
  },
  methods: {
    fmtAmount, fmtDateTime,
    async loadCurrencies() {
      this.currenciesLoading = true;
      try {
        const res = await this.$axios({ method: 'get', url: '/common/currencies', loadingBar: false });
        const list = Array.isArray(res.data) ? res.data : [];
        this.currencies = list.filter(c => c && c.code);
      } catch (e) {
        this.currencies = [];
      } finally {
        this.currenciesLoading = false;
      }
      // 기본값은 기관 통화. 목록을 못 받았으면 기관 통화 하나만 선택지로 둔다
      if (!this.currencies.length && this.orgCurrency) this.currencies = [{ code: this.orgCurrency }];
      if (this.orgCurrency && this.currencies.some(c => c.code === this.orgCurrency)) this.form.currency_code = this.orgCurrency;
      else if (this.currencies.length) this.form.currency_code = this.currencies[0].code;
    },
    async submit() {
      if (!this.canSubmit) return;
      this.submitting = true;
      this.error = '';
      try {
        const data = { amount: String(this.form.amount), currency_code: this.form.currency_code };
        if (this.form.memo) data.memo = this.form.memo;
        const res = await this.$axios({ method: 'post', url: `/voucher/org/${this.orgIdx}/purchases`, data, loadingBar: true });
        this.result = res.data || {};
        this.$emit('requested', this.result);
      } catch (e) {
        this.error = voucherErrorText(e, '구매 요청을 보내지 못했습니다.');
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../_org.scss';

.p-done {
  text-align: left;
  &__ref-label { font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: $p-ink-soft; }
  &__ref {
    font-family: $p-font-mono;
    font-size: 26px;
    font-weight: 800;
    color: $p-ink;
    padding: 10px 14px;
    border: 1px dashed $p-line-hard;
    border-radius: 10px;
    background: $p-point-soft;
    display: inline-block;
    margin-top: 6px;
  }
}
.p-pre { white-space: pre-wrap; }
</style>
